import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { SEOHelmet } from 'components/SEO';
import { UserIdentityBlock } from 'components/UserIdentityBlock';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import CommitmentConflictDialog from 'components/CommitmentConflictDialog/CommitmentConflictDialog';
import EditHaveListCardModal from 'components/EditHaveListCardModal/EditHaveListCardModal';
import EnableTradeOffersModal from 'components/EnableTradeOffersModal';
import { useGetPublicProfileQuery, useGetUserTradeStatsQuery } from 'store/services/profile/profileApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import {
  useCancelTradeOfferMutation,
  useCreateTradeOfferMutation,
  useLazyGetMyCommittedQuantitiesQuery,
} from 'store/services/tradeOffers/tradeOffersApi';
import {
  UpdateHaveListCardRequest,
  useUpdateHaveListCardMutation,
} from 'store/services/collectionList/haveList/haveListApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { MTGCard } from 'types/interfaces/mtgCard';
import { OfferLineItem } from 'types/interfaces/tradeOffer';
import { CommitmentConflict, findHaveIdConflicts } from 'utils/commitmentConflicts';
import { parseOfferError } from 'utils/offerErrors';
import { useWindowSize } from 'hooks/useWindowSize';
import { useCardSizePreference } from 'hooks/useCardSizePreference';
import CardSizeSelector from 'components/CardSizeSelector/CardSizeSelector';
import { OfferBuilderProvider, useOfferBuilder } from './context/OfferBuilderContext';
import SourcePanel from './components/SourcePanel';
import OfferColumn from './components/OfferColumn';
import OfferCommitBar from './components/OfferCommitBar';
import './OfferBuilderPage.css';

// Below this width four columns cannot breathe; collapse to the stepped flow
// over the SAME state — one data model, one propose handler, one payload shape.
const FOUR_COLUMN_MIN_WIDTH = 1100;

type BuilderStep = 1 | 2 | 3;

// EditHaveListCardModal is prop-driven on a have-list row; the modal only reads
// the identity/edit fields, so a view-model built from the line item suffices.
const toCollectionListRow = (item: OfferLineItem): CollectionList => ({
  id: item.userMTGCardHaveId,
  userId: 0,
  quantity: item.ownedQuantity,
  foil: item.foil,
  condition: item.condition as CardCondition,
  forTrade: true,
  createdAt: new Date(),
  card: {
    id: item.card.id,
    name: item.card.name,
    lang: item.card.lang,
    scryfallID: item.card.scryfallId || '',
    setCode: item.card.setCode,
    setName: item.card.setName,
    collectorNumber: item.card.collectorNumber,
    oracleId: item.card.oracleId,
  } as unknown as MTGCard,
});

// Half-banner avatars: both come from data the page already fetches
// (getMe for the viewer, getPublicProfile for the counterparty) — no new request.
interface BannerAvatar {
  avatarUrl: string | null;
  username?: string;
  /** Drives the flag badge. Both profile payloads carry it; passing it is the
   *  whole reason a flag renders here at all. */
  countryCode?: string | null;
  isElite: boolean;
  showAnimatedBorder: boolean;
  borderColor1: string | null;
  borderColor2: string | null;
  isLoading?: boolean;
}

interface BuilderContentProps {
  viewerPoints: number | null;
  counterpartyPoints: number | null;
  counterpartyRating: number | null;
  counterpartyRatingCount: number;
  counterpartyCardsSent: number | null;
  counterpartyIsElite: boolean;
  fullHaveListPublic: boolean;
  viewerAvatar: BannerAvatar;
  counterpartyAvatar: BannerAvatar;
}

/**
 * Load-path continuation: enabling the flag invalidates the PrivacyPreferences
 * tag and RTK Query refetches the candidates by itself, so there is nothing for
 * the continuation to do except let the dialog close. Module-level so its
 * identity is stable across renders.
 */
const noopContinuation = () => {};

const OfferBuilderContent: React.FC<BuilderContentProps> = ({
  viewerPoints,
  counterpartyPoints,
  counterpartyRating,
  counterpartyRatingCount,
  counterpartyCardsSent,
  counterpartyIsElite,
  fullHaveListPublic,
  viewerAvatar,
  counterpartyAvatar,
}) => {
  const navigate = useNavigate();
  const {
    counterpartyId,
    counterpartyUsername,
    youSend,
    theySend,
    suggestionId,
    clearAll,
    updateItemOwnedQuantity,
    setRejectedHaveId,
  } = useOfferBuilder();

  const { width } = useWindowSize();
  const isStepped = width > 0 && width < FOUR_COLUMN_MIN_WIDTH;
  const [step, setStep] = useState<BuilderStep>(1);
  const [cardSize, setCardSize] = useCardSizePreference('offerBuilder.cardSize');

  // Fit the builder to the viewport so the commit bar is never below the fold:
  // the app shell grows with content and body-scrolls, so pure CSS cannot know
  // the builder's top offset (the verification banner shifts it per account).
  const builderRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = builderRef.current;
    if (!el) return;
    if (isStepped) {
      el.style.height = '';
      return;
    }
    const update = () => {
      const documentTop = el.getBoundingClientRect().top + window.scrollY;
      el.style.height = `${Math.max(420, window.innerHeight - documentTop - 8)}px`;
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isStepped]);

  const [createOffer, { isLoading: isCreating }] = useCreateTradeOfferMutation();
  const [cancelOffer] = useCancelTradeOfferMutation();
  const [fetchCommitted, { isFetching: isCheckingCommitted }] =
    useLazyGetMyCommittedQuantitiesQuery();
  const [updateHaveListCard] = useUpdateHaveListCardMutation();

  const [conflicts, setConflicts] = useState<CommitmentConflict[]>([]);
  /**
   * Set when the server rejects with PROPOSER_OFFERS_DISABLED — the viewer has
   * turned off their own trade offers, which under the reciprocity rule also
   * stops them sending. The stored callback is the action to resume once the
   * flag is back on, so enabling continues what they were doing instead of
   * dropping them at square one. Two producers: the propose click below, and
   * the candidates fetch in SourcePanel (the builder can 403 on LOAD).
   */
  const [offersDisabledContinuation, setOffersDisabledContinuation] = useState<
    (() => void | Promise<void>) | null
  >(null);
  const [editTarget, setEditTarget] = useState<OfferLineItem | null>(null);
  const [isCancellingOffer, setIsCancellingOffer] = useState(false);

  /**
   * LOAD-path entry to the same dialog. The candidates fetch 403'd because the
   * viewer's own offers are off, so the continuation is "let the panels load",
   * NOT a proposal — there is nothing to propose yet. Enabling invalidates the
   * PrivacyPreferences tag; RTK Query refetches the candidates on its own, so
   * the continuation only has to close the dialog. The builder's sessionStorage
   * state is untouched throughout, so an in-progress offer survives.
   *
   * Stored as a no-op thunk rather than left null so the dialog's isOpen check
   * (`!== null`) stays a single rule for both entry points.
   */
  const handleProposerOffersDisabledOnLoad = React.useCallback(() => {
    // The functional updater form is what keeps this idempotent: the panel's
    // effect can fire on re-render, and it must not clobber a continuation the
    // propose path already stored. Note the double arrow — a function stored in
    // state has to be wrapped, or React would call it as an updater.
    setOffersDisabledContinuation((prev) => prev ?? noopContinuation);
  }, []);

  const doCreate = async () => {
    await createOffer({
      recipientId: counterpartyId,
      proposerItems: youSend.map((i) => ({
        userMTGCardHaveId: i.userMTGCardHaveId,
        quantity: i.quantity,
        // Omit-when-equal: an untouched selector produces today's payload
        // byte for byte; the server reads an absent condition as the row's.
        ...(i.declaredCondition && i.declaredCondition !== i.condition
          ? { condition: i.declaredCondition }
          : {}),
      })),
      recipientItems: theySend.map((i) => ({
        userMTGCardHaveId: i.userMTGCardHaveId,
        quantity: i.quantity,
      })),
      ...(suggestionId ? { suggestionId } : {}),
    }).unwrap();
    toast.success(`Trade offer sent to ${counterpartyUsername}`);
    clearAll();
    navigate('/trade-offers');
  };

  const handlePropose = async () => {
    try {
      // Fresh read every time: another tab or an accepted offer can change
      // commitments between clicks (preferCacheValue=false).
      const committed = await fetchCommitted(undefined, false).unwrap();
      const found = findHaveIdConflicts(
        youSend.map((i) => ({
          userMTGCardHaveId: i.userMTGCardHaveId,
          quantity: i.quantity,
          ownedQuantity: i.ownedQuantity,
          cardName: i.card.name,
        })),
        committed.byHaveId
      );
      if (found.length > 0) {
        setConflicts(found);
        return;
      }
      await doCreate();
    } catch (err) {
      const parsed = parseOfferError(err);
      if (parsed.kind === 'proposer_offers_disabled') {
        // Recoverable in place. Resume the PROPOSAL itself, not a reload: the
        // builder's state is intact, so the user lands back on the same offer.
        setOffersDisabledContinuation(() => doCreate);
        return;
      }
      if (parsed.kind === 'exceeds_want_quantity' && parsed.exceedsWant) {
        // Mark the offending line as well as toasting. The message names a
        // card, but the same card in three sets is three lines — only the
        // have-row id says WHICH one to remove. Unreachable through the UI
        // (the panel gates the add), so this covers a stale session, a want
        // the recipient shrank mid-build, or a client bypass.
        setRejectedHaveId(parsed.exceedsWant.userMTGCardHaveId);
      }
      toast.error(parsed.message);
    }
  };

  const handleCancelConflictingOffers = async () => {
    setIsCancellingOffer(true);
    try {
      const offerIds = Array.from(new Set(conflicts.flatMap((c) => c.offerIds)));
      for (const offerId of offerIds) {
        await cancelOffer(offerId).unwrap();
      }
      setConflicts([]);
      await handlePropose();
    } catch (err) {
      toast.error(parseOfferError(err).message);
    } finally {
      setIsCancellingOffer(false);
    }
  };

  const handleEditHaveList = () => {
    const target = conflicts
      .map((c) => youSend.find((i) => i.userMTGCardHaveId === c.userMTGCardHaveId))
      .find(Boolean);
    setConflicts([]);
    if (target) setEditTarget(target);
  };

  const handleEditSave = async (updates: UpdateHaveListCardRequest) => {
    await updateHaveListCard(updates).unwrap();
    if (editTarget && typeof updates.quantity === 'number') {
      updateItemOwnedQuantity('youSend', editTarget.userMTGCardHaveId, updates.quantity);
    }
  };

  const isProposing = isCreating || isCheckingCommitted;

  // The counterparty's full identity treatment, matching /sendcards: 48px avatar
  // with country flag and Elite border, username + Elite badge, point balance,
  // and the UserHoverCard overlay on hover. The rating line rides in the block's
  // one child slot so it reads as secondary to the identity above it.
  const trustLine = (
    <UserIdentityBlock
      username={counterpartyUsername}
      avatarUrl={counterpartyAvatar.avatarUrl}
      country={counterpartyAvatar.countryCode}
      points={counterpartyPoints}
      isElite={counterpartyIsElite}
      showAnimatedBorder={counterpartyAvatar.showAnimatedBorder}
      borderColor1={counterpartyAvatar.borderColor1}
      borderColor2={counterpartyAvatar.borderColor2}
      className="offer-builder-trust"
    >
      <span className="offer-builder-trust-stats">
        {counterpartyRating != null
          ? `★ ${counterpartyRating.toFixed(1)} (${counterpartyRatingCount})`
          : 'No ratings yet'}
        {counterpartyCardsSent != null && ` · ${counterpartyCardsSent.toLocaleString()} cards sent`}
      </span>
    </UserIdentityBlock>
  );

  const footer = (
    <OfferCommitBar
      viewerPoints={viewerPoints}
      counterpartyPoints={counterpartyPoints}
      isProposing={isProposing}
      onPropose={handlePropose}
    />
  );

  const youSendGuidance = {
    arrowDirection: 'left' as const,
    addLine: 'Add cards from the panel on the left.',
    relationLine: `Those are cards ${counterpartyUsername} wants that you already own.`,
  };
  const theySendGuidance = {
    arrowDirection: 'right' as const,
    addLine: 'Add cards from the panel on the right.',
    relationLine: `Those are cards ${counterpartyUsername} owns that are on your want list.`,
  };

  return (
    <div ref={builderRef} className={`offer-builder size-${cardSize.toLowerCase()}`}>
      {!isStepped ? (
        <>
          {/* The working area scrolls internally; the commit bar below it is a
              pinned flex footer, so everything that resolves the screen stays
              visible from first paint (the app shell's flex scroller defeats
              position: sticky, hence this structure). */}
          <div className="offer-builder-scroll">
            <header className="offer-builder-header">
              <h1 className="offer-builder-heading">Build a trade with {counterpartyUsername}</h1>
            {/* The only new element when a suggestion seeds the builder. Both
                browse panels, the steppers, the condition selector, the S/M/L
                control and the footer behave exactly as they do unseeded. */}
            {suggestionId && (
              <span className="offer-builder-seeded-chip">Seeded from a suggested trade</span>
            )}
              {trustLine}
              <CardSizeSelector
                value={cardSize}
                onChange={setCardSize}
                className="offer-builder-size"
              />
            </header>
            {/* The four mandated columns read as two halves: YOU ADD spans the
                left source + You send; THEY ADD spans They send + the right
                source. Each source sits adjacent to the column it fills — their
                want list holds cards YOU send; their have list holds cards THEY
                send. The participant avatars bookend the row (viewer far left,
                counterparty far right) and are decorative: names are stated
                elsewhere on the page, so they are hidden from screen readers. */}
            <div className="offer-half-headers">
              <div className="offer-half offer-half--yours">
                <span className="offer-half-avatar" aria-hidden="true">
                  <AvatarWithFlag
                    avatarUrl={viewerAvatar.avatarUrl}
                    username={viewerAvatar.username}
                    countryCode={viewerAvatar.countryCode}
                    size={28}
                    alt=""
                    isLoading={viewerAvatar.isLoading}
                    isElite={viewerAvatar.isElite}
                    showAnimatedBorder={viewerAvatar.showAnimatedBorder}
                    borderColor1={viewerAvatar.borderColor1}
                    borderColor2={viewerAvatar.borderColor2}
                  />
                </span>
                <span className="offer-half-title">YOU ADD</span>
                <span className="offer-half-sub">cards leaving your collection</span>
                <span className="offer-half-arrow" aria-hidden="true">
                  →
                </span>
              </div>
              <div className="offer-half offer-half--theirs">
                <span className="offer-half-arrow" aria-hidden="true">
                  ←
                </span>
                <span className="offer-half-title">THEY ADD</span>
                <span className="offer-half-sub">cards coming to you</span>
                <span className="offer-half-avatar" aria-hidden="true">
                  <AvatarWithFlag
                    avatarUrl={counterpartyAvatar.avatarUrl}
                    username={counterpartyAvatar.username}
                    countryCode={counterpartyAvatar.countryCode}
                    size={28}
                    alt=""
                    isElite={counterpartyAvatar.isElite}
                    showAnimatedBorder={counterpartyAvatar.showAnimatedBorder}
                    borderColor1={counterpartyAvatar.borderColor1}
                    borderColor2={counterpartyAvatar.borderColor2}
                  />
                </span>
              </div>
            </div>
            <div className="offer-builder-grid">
              <SourcePanel side="send" />
              <OfferColumn side="youSend" title="You send" emptyGuidance={youSendGuidance} />
              <OfferColumn side="theySend" title="They send" emptyGuidance={theySendGuidance} />
              <SourcePanel
                side="receive"
                fullHaveListPublic={fullHaveListPublic}
                onProposerOffersDisabled={handleProposerOffersDisabledOnLoad}
              />
            </div>
          </div>
          {footer}
        </>
      ) : (
        <div className="offer-builder-stepped">
          <header className="offer-builder-header">
            <h1 className="offer-builder-heading">Build a trade with {counterpartyUsername}</h1>
            {/* The only new element when a suggestion seeds the builder. Both
                browse panels, the steppers, the condition selector, the S/M/L
                control and the footer behave exactly as they do unseeded. */}
            {suggestionId && (
              <span className="offer-builder-seeded-chip">Seeded from a suggested trade</span>
            )}
            {trustLine}
            <CardSizeSelector
              value={cardSize}
              onChange={setCardSize}
              className="offer-builder-size"
            />
          </header>
          <div className="offer-builder-steps-nav" role="tablist" aria-label="Builder steps">
            {[1, 2, 3].map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={step === s}
                className={`offer-step-pill${step === s ? ' active' : ''}`}
                onClick={() => setStep(s as BuilderStep)}
              >
                {s === 1 ? '1. You send' : s === 2 ? '2. They send' : '3. Review'}
              </button>
            ))}
          </div>
          {step === 1 && (
            <>
              <SourcePanel side="send" />
              <OfferColumn side="youSend" title="You send" emptyGuidance={youSendGuidance} />
              <button type="button" className="offer-step-next" onClick={() => setStep(2)}>
                Next: pick what they send
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <SourcePanel
                side="receive"
                fullHaveListPublic={fullHaveListPublic}
                onProposerOffersDisabled={handleProposerOffersDisabledOnLoad}
              />
              <OfferColumn side="theySend" title="They send" emptyGuidance={theySendGuidance} />
              <button type="button" className="offer-step-next" onClick={() => setStep(3)}>
                Next: review and propose
              </button>
            </>
          )}
          {step === 3 && (
            <>
              <OfferColumn side="youSend" title="You send" emptyGuidance={youSendGuidance} />
              <OfferColumn side="theySend" title="They send" emptyGuidance={theySendGuidance} />
              {footer}
            </>
          )}
        </div>
      )}

      <EnableTradeOffersModal
        isOpen={offersDisabledContinuation !== null}
        onClose={() => setOffersDisabledContinuation(null)}
        onContinue={() => {
          const resume = offersDisabledContinuation;
          setOffersDisabledContinuation(null);
          return resume?.();
        }}
      />
      <CommitmentConflictDialog
        isOpen={conflicts.length > 0}
        conflicts={conflicts}
        isCancellingOffer={isCancellingOffer}
        onAbort={() => setConflicts([])}
        onCancelConflictingOffers={handleCancelConflictingOffers}
        onEditHaveList={handleEditHaveList}
      />
      <EditHaveListCardModal
        isOpen={editTarget !== null}
        card={editTarget ? toCollectionListRow(editTarget) : null}
        onClose={() => setEditTarget(null)}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default function OfferBuilderPage() {
  const { username } = useParams<{ username: string }>();
  const { data: me, isLoading: isMeLoading } = useGetMeQuery();
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useGetPublicProfileQuery(username || '', { skip: !username });
  const { data: stats } = useGetUserTradeStatsQuery(username || '', { skip: !username });

  const isSelf = useMemo(
    () => !!me && !!profile && me.id === profile.id,
    [me, profile]
  );

  return (
    <>
      <SEOHelmet
        title={`Build a Trade Offer${username ? ` with ${username}` : ''} - TradeMagic`}
        description="Compose a two-sided trade offer: pick cards from their want list and have list, see both totals, and propose with prices locked."
      />
      {isProfileLoading ? (
        <div className="offer-builder-status">Loading trader…</div>
      ) : profileError || !profile ? (
        <div className="offer-builder-status">
          This trader could not be found. Check the link and try again.
        </div>
      ) : isSelf ? (
        <div className="offer-builder-status">You can&apos;t build a trade offer with yourself.</div>
      ) : (
        <OfferBuilderProvider
          counterpartyId={profile.id}
          counterpartyUsername={profile.username}
          viewerPoints={me?.points ?? null}
          counterpartyPoints={profile.points ?? null}
        >
          <OfferBuilderContent
            viewerPoints={me?.points ?? null}
            counterpartyPoints={profile.points ?? null}
            counterpartyRating={profile.averageRating}
            counterpartyRatingCount={profile.totalRatings}
            counterpartyCardsSent={stats?.cardsSent ?? null}
            counterpartyIsElite={!!profile.isElite}
            fullHaveListPublic={stats ? stats.hasPublicHaveList : true}
            viewerAvatar={{
              avatarUrl: me?.avatarUrl ?? null,
              username: me?.username,
              countryCode: me?.country ?? null,
              isElite: !!me?.isElite,
              showAnimatedBorder: !!me?.showAnimatedBorder,
              borderColor1: me?.borderColor1 ?? null,
              borderColor2: me?.borderColor2 ?? null,
              isLoading: isMeLoading,
            }}
            counterpartyAvatar={{
              avatarUrl: profile.avatarUrl,
              username: profile.username,
              countryCode: profile.country,
              isElite: !!profile.isElite,
              showAnimatedBorder: !!profile.showAnimatedBorder,
              borderColor1: profile.borderColor1 ?? null,
              borderColor2: profile.borderColor2 ?? null,
            }}
          />
        </OfferBuilderProvider>
      )}
    </>
  );
}
